import { useState } from 'react';
import { addDays } from 'date-fns';
import { toDate } from 'date-fns-tz';
import api from 'api';
import {
  getDefaultEmailTemplate,
  renderPlaceholdersInTemplates
} from 'shared-helpers/email';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import { ModalButtonWithForm } from 'components/common';
import {
  Datepicker,
  EmailMessageEditor,
  FormGroup,
  RadioGroup,
  UserSelector
} from 'components/common/form';
import { UserPasswordReview } from 'components/users';

import { EmailMessageTemplateContent } from 'shared-types';
import { GetEmailTemplatesByTypeRes } from 'shared-types/responses';

type UsersAdd = {
  email?: EmailMessageTemplateContent;
  emails?: string[];
  passwords?: string[];
  passwordsAll?: string;
  recipientsType?: 'all' | 'new';
  userExpirationDate?: Date | null;
  usernames?: string[];
  userSelector: 'emails' | 'usernames';
};

interface Props {
  allowGroupSnapshots?: boolean;
  existingUserIds?: number[];
  updateUsers: () => Promise<void>;
}

const CalendarEventParticipantsAdd = ({
  allowGroupSnapshots = false,
  existingUserIds,
  updateUsers
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { calendarEvent },
    serverConfig: {
      accountAddForceExpirationDate,
      accountAddForceExpirationDateMaxDays,
      adminUrl,
      timeZone
    },
    user
  } = useReduxState();

  const [newUsers, setNewUsers] = useState<string[]>([]);
  const [userPasswords, setUserPasswords] = useState({});

  const { data: emailTemplatesData } = useApiData<GetEmailTemplatesByTypeRes>({
    url: `/admin/email-templates-data?type=calendar-event-access&calendarEventId=${calendarEvent.id}`
  });

  let emailTemplates = emailTemplatesData?.emailTemplates || [];

  emailTemplates = renderPlaceholdersInTemplates({
    emailTemplates,
    overridePlaceholderValues: {
      contactEmail: user?.email,
      eventTitle: calendarEvent.title,
      eventUrl: `${adminUrl}/calendar/${calendarEvent.id}`,
      startDate: getFormattedDate({
        date: calendarEvent.startDate,
        dateFormat: 'dd.MM.yyyy'
      })
    }
  });

  const emailTemplatesDefault = getDefaultEmailTemplate({
    language,
    emailTemplates
  });

  const handleSubmit = async (values: UsersAdd) => {
    try {
      const response = await api.post(
        `/admin/calendar-events/${calendarEvent.id}/users`,
        {
          ...values,
          userExpirationDate: values.userExpirationDate
            ? toDate(values.userExpirationDate, { timeZone })
            : null
        }
      );
      await updateUsers();

      dispatch.updatePageState({
        calendarEvent: {
          ...calendarEvent,
          numberOfParticipantsAdded:
            calendarEvent.numberOfParticipantsAdded +
            response.data.numberOfAddedUsers
        }
      });

      dispatch.addNotice({
        text: messages.commonAddUsersSuccess,
        type: 'success'
      });

      if (values.usernames?.length && response.data?.newUsers) {
        const userPasswords = {};
        values.passwords.forEach((password, i) => {
          if (password) userPasswords[values.usernames[i]] = password;
        });

        setUserPasswords(userPasswords);
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const initialValues = emailTemplatesDefault
    ? {
        email: {
          ...emailTemplatesDefault.email,
          selectedTemplateIdentifier: emailTemplatesDefault.identifier
        },
        recipientsType: 'all',
        userExpirationDate: null
      }
    : {};

  return (
    <>
      <ModalButtonWithForm
        buttonCategory="success"
        buttonIcon="plus"
        buttonSize="header"
        buttonText={messages.commonAddUsers}
        initialValues={initialValues}
        modalTitle={messages.commonAddUsers}
        modalActionText={messages.commonAdd}
        modalWidth="wide"
        onSubmit={handleSubmit}
        pristineIsValid={false}
      >
        {(formState) => (
          <>
            <UserSelector
              allowGroupSnapshots={allowGroupSnapshots}
              existingUsers={existingUserIds || []}
              onNewUsers={setNewUsers}
              required={true}
            />

            {accountAddForceExpirationDate && newUsers.length > 0 && (
              <FormGroup
                htmlFor="userExpirationDate"
                label={messages.commonExpirationDateForNewUserAccounts}
                maxWidth="100%"
              >
                <Datepicker
                  field="userExpirationDate"
                  id="userExpirationDate"
                  isClearable={false}
                  maxDate={addDays(
                    dateNow,
                    accountAddForceExpirationDateMaxDays
                  )}
                  minDate={dateNow}
                  required={true}
                  validateOnChange={true}
                  validateOnBlur={true}
                  style={{ maxWidth: 200 }}
                  placeholder={messages.commonExpirationDate}
                />
              </FormGroup>
            )}

            {emailTemplatesDefault &&
            formState.values?.userSelector !== 'usernames' ? (
              <>
                <FormGroup label={messages.emailRecipientsSend}>
                  <RadioGroup
                    field="recipientsType"
                    options={[
                      {
                        id: 'email-recipients-all',
                        label: messages.emailRecipientsAllAbove,
                        value: 'all'
                      },
                      {
                        id: 'email-recipients-new',
                        label: messages.emailRecipientsNew,
                        value: 'new'
                      }
                    ]}
                  />
                </FormGroup>

                <EmailMessageEditor
                  availableTemplates={emailTemplates}
                  id="calendar-event-participants-add"
                  style={{ marginTop: 20 }}
                />
              </>
            ) : null}
          </>
        )}
      </ModalButtonWithForm>

      <UserPasswordReview
        onModalDismiss={() => setUserPasswords({})}
        passwords={userPasswords}
      />
    </>
  );
};

export default CalendarEventParticipantsAdd;
